import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    teacherId: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.teacherId?.trim()) {
      newErrors.teacherId = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.teacherId?.trim())) {
      newErrors.teacherId = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await signIn(formData?.teacherId, formData?.password);
      
      if (error) {
        setErrors({
          submit: error?.message || 'Invalid credentials. Please try again.'
        });
      } else {
        // Successful login - navigate to dashboard
        navigate('/teacher-dashboard');
      }
    } catch (error) {
      setErrors({
        submit: error?.message?.toLowerCase()?.includes('fetch') || error instanceof TypeError
          ? 'Unable to connect to the server. Your Supabase project may be paused — visit supabase.com/dashboard to resume it.' :'An error occurred during login. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
  };

  return (
    <div className="w-full">
      <div className="mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
          Welcome Back
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Sign in to access your teaching dashboard
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <div className="p-3 bg-primary/10 border border-primary/20 rounded-md mb-4">
          <p className="text-xs font-medium text-primary mb-2">Demo Credentials:</p>
          <p className="text-xs text-muted-foreground">Email: teacher@example.com | Password: password123</p>
          <p className="text-xs text-muted-foreground">Email: demo@teacher.com | Password: demo123</p>
        </div>

        <Input
          label="Email"
          type="email"
          name="teacherId"
          id="login-email"
          autoComplete="email"
          placeholder="Enter your email address"
          value={formData?.teacherId}
          onChange={handleChange}
          error={errors?.teacherId}
          required
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            id="login-password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-organic"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label="Remember me"
            checked={formData?.rememberMe}
            onChange={handleCheckboxChange}
          />
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-organic"
          >
            Forgot Password?
          </button>
        </div>

        {errors?.submit && (
          <div className="p-3 md:p-4 bg-error/10 border border-error/20 rounded-md flex items-start">
            <Icon name="AlertCircle" size={20} className="text-error mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{errors?.submit}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={loading}
          className="mt-6"
        >
          Sign In
        </Button>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} className="text-success" />
            <span>SSL Encrypted</span>
            <span className="mx-2">•</span>
            <Icon name="Lock" size={16} className="text-success" />
            <span>FERPA Compliant</span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;